
  import { computed, defineComponent, onMounted, ref } from 'vue';
  import { ErrorMessage, Field, Form } from 'vee-validate';
  import { setCurrentPageBreadcrumbs } from '@/core/helpers/breadcrumb';
  import { useI18n } from 'vue-i18n/index';
  import { useStore } from 'vuex';
  import { useRouter } from 'vue-router';
  import { Actions } from '@/store/enums/StoreEnums';
  import { useAbility } from '@casl/vue';
  import Swal from 'sweetalert2/dist/sweetalert2.js';
  import * as Yup from 'yup';
  import { AdsPackageObject } from '@/store/modules/Subscriptions/AdsPackageModule';

  export default defineComponent({
    name: 'package-creating',
    components: {
      ErrorMessage,
      Field,
      Form,
    },
    async setup() {
      const { t, te } = useI18n();
      const { can } = useAbility();
      const store = useStore();
      const router = useRouter();
      const submitButton = ref<HTMLButtonElement | null>(null);
      const multiselect = ref<{ noOptions: boolean } | null>(null);
      const selectedpackages = ref<Array<number>>([]);
      const loading = ref(false);
      const createdPackage = ref<AdsPackageObject | null>(null);
      const price = ref<number | null>(null);
      const discountPercentage = ref<number | null>(null);
      const color = ref<string>('#000000');
      const viewColor = ref<string>('#000000');
      const priceColor = ref<string>('#000000');

      enum PackagesTypes {
        MIX = 1,
        SUBSCRIPTIONS = 2,
        CREDIT = 3,
      }

      const packageTypeOptions = computed(() => {
        return Object.entries(PackagesTypes)
          .filter(([key]) => isNaN(Number(key)))
          .map(([key, value]) => [key, Number(value)]);
      });

      const offeredPrice = computed(() => {
        if (price.value !== null && discountPercentage.value !== null) {
          return price.value - (price.value * discountPercentage.value) / 100;
        }
        return null;
      });

      const translate = (text) => {
        if (te(text)) {
          return t(text);
        } else {
          return text;
        }
      };

      const goBack = () => {
        router.back();
      };

      const packageSchema = Yup.object().shape({
        titleAr: Yup.string().required(() =>
          translate('AR_TITLE_IS_REQUIRED_FIELD')
        ),
        titleEn: Yup.string().required(() =>
          translate('EN_TITLE_IS_REQUIRED_FIELD')
        ),
        price: Yup.number().required(() =>
          translate('PRICE_IS_REQUIRED_FIELD')
        ),
        offeredPrice: Yup.number().required(() =>
          translate('OFFERED_PRICE_IS_REQUIRED_FIELD')
        ),
      });

      const onSubmitCreate = async (values) => {
        if (submitButton.value) {
          // eslint-disable-next-line
          submitButton.value!.disabled = true;
          submitButton.value.setAttribute('data-kt-indicator', 'on');
        }
        // values.offeredPrice = offeredPrice.value;
        if (values.price) values.price = Number(values.price);
        if (values.discountPercentage)
          values.discountPercentage = Number(values.discountPercentage);
        if (values.offeredPrice)
          values.offeredPrice = Number(values.offeredPrice);
        if (values.type) values.type = Number(values.type);
        if (values.iveCredits) values.iveCredits = Number(values.iveCredits);
        if (values.adsCredits) values.adsCredits = Number(values.adsCredits);
        if (values.daysCovered) values.daysCovered = Number(values.daysCovered);
        if (values.sort) values.sort = Number(values.sort);
        if (values.realtedTypeSlug)
          values.realtedTypeSlug = Number(values.realtedTypeSlug);
        if (!values.color) values.color = color.value;
        if (!values.viewColor) values.viewColor = viewColor.value;
        if (!values.priceColor) values.priceColor = priceColor.value;

        const result = await store.dispatch(Actions.CREATE_PACKAGE, values);
        const [errorName] = Object.keys(store.getters.getErrors);
        const error = store.getters.getErrors[errorName];
        if (error) {
          Swal.fire({
            text: translate(error[0]),
            icon: 'error',
            buttonsStyling: false,
            confirmButtonText: translate('tryAgainExcl'),
            customClass: {
              confirmButton: 'btn fw-bold btn-light-danger',
            },
          });
          //Deactivate indicator
          submitButton.value?.removeAttribute('data-kt-indicator');
          // eslint-disable-next-line
          submitButton.value!.disabled = false;
        } else {
          createdPackage.value = result;
          Swal.fire({
            text: translate('SUCCESSFULLY_ADDED_PACKAGE'),
            icon: 'success',
            buttonsStyling: false,
            confirmButtonText: translate('ok'),
            customClass: {
              confirmButton: 'btn btn-light-primary',
            },
          }).then(() => {
            router.push({ name: 'packages-listing' });
          });
        }
      };

      onMounted(() => {
        setCurrentPageBreadcrumbs(translate('packageCreating'), [
          translate('packages'),
        ]);
      });

      const multiSelectOpened = async () => {
        loading.value = true;
        if (multiselect.value?.noOptions) {
          await store.dispatch(Actions.GET_PACKAGES, { limit: 50 });
        }
        loading.value = false;
      };

      store.dispatch(Actions.REMOVE_BODY_CLASSNAME, 'page-loading-over');

      return {
        packages: computed(() => store.getters.packagesList),
        onSubmitCreate,
        submitButton,
        discountPercentage,
        offeredPrice,
        price,
        translate,
        packageSchema,
        goBack,
        can,
        selectedpackages,
        multiSelectLabel: 'title',
        loading,
        multiSelectOpened,
        multiselect,
        createdPackage,
        packageTypeOptions,
        color,
        viewColor,
        priceColor,
      };
    },
  });
